import { useEffect, useState } from "react";

import {
  Button,
  TextArea,
  Table,
  TableMetadata,
  Typography,
  useTable,
  Label,
} from "@king-ict/eupisi-ui/components";
import { hrDate } from "@king-ict/eupisi-ui/utils";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";

import { DEFAULT_INSTITUTION_COMMITTEE } from "./constants";
import { addRowKeyIdToTableData, generateCommitteeTableColumns } from "./utils";
import {
  getInstitutionCommitteeById,
  changeInstitutionCommitteeStatus,
} from "../../api/Committee/InstitutionCommitteeApi";
import { getSchoolYearCurrent } from "../../api/SchoolYear/SchoolYearApi";
import {
  ContentArea,
  FormCard,
  ToolbarEdit,
  ToolbarTop,
} from "../../components";
import { useEnumCodeBooks } from "../../hooks/useEnumCodeBooks";
import {
  CommitteeStatus,
  CommitteeEmployee,
  InstitutionCommittee,
} from "../../models/Committee";
import { ImportantDateEventEnum } from "../../models/ImportantDate/ImportantDateEventEnum";
import { committeeEditUrl, committeeListRoute } from "../../routes/routes";
import { FormatUtils } from "../../utils";

export function CommitteeDetails() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const history = useHistory();

  const [item, setItem] = useState<InstitutionCommittee>(
    DEFAULT_INSTITUTION_COMMITTEE
  );
  const [tableData, setTableData] = useState<CommitteeEmployee[]>([]);
  const [isEditable, setIsEditable] = useState(false);

  const tableMetadata: TableMetadata<CommitteeEmployee> = {
    rowKey: "rowKeyId",
    columns: generateCommitteeTableColumns(t),
    rowToolTip: () => undefined,
  };

  const [tableProps] = useTable({
    metadata: tableMetadata,
    data: tableData,
    pagination: false,
    paginationPage: undefined,
    paginationPageSize: undefined,
    totalItems: 1,
    defaultSort: {},
  });
  const { committeeStatusCodeBook: statuses } = useEnumCodeBooks();

  useEffect(() => {
    (async () => {
      const schoolYear = await getSchoolYearCurrent();

      if (schoolYear) {
        const importantDate = schoolYear.importantDates.find(
          (val) =>
            val.eventId === ImportantDateEventEnum.InstitutionAdministration
        );

        if (importantDate) {
          const exactlyNow = new Date(Date.now());
          const dateFrom = new Date(importantDate.dateTimeFrom);
          const dateTo = new Date(importantDate.dateTimeTo);

          if (dateFrom <= exactlyNow && dateTo >= exactlyNow) {
            setIsEditable(true);
          }
        }
      }
      if (!isNaN(+id) && +id > 0) {
        var result = await getInstitutionCommitteeById(+id);
        if (result) {
          setItem(result);
          setTableData(addRowKeyIdToTableData(result));
        }
      }
    })();
  }, [id]);

  const handleBackClicked = () => {
    goBack();
  };
  const handleEditClicked = () => {
    history.push(committeeEditUrl(+id));
  };
  const handleForApprovalClicked = async () => {
    const idNumber = Number.parseInt(id);
    const status = CommitteeStatus.ConfirmationRequired;
    const result = await changeInstitutionCommitteeStatus(idNumber, status);
    if (result && result.isValid) {
      setItem(result.result!);
    }
  };
  const goBack = () => {
    history.push(committeeListRoute);
  };
  return (
    <ContentArea>
      <ToolbarTop
        showAdd={"hide"}
        showBack={"show"}
        showEdit={isEditable ? "show" : "disable"}
        pageTitle={t("common.expertCommission.pageNameEdit")}
        onClickEdit={handleEditClicked}
        onClickBack={handleBackClicked}
      />

      <FormCard>
        <Row>
          <Col md={2}>
            <Typography variant="base" color="light">
              {t("common.expertCommission.institution")}
            </Typography>
          </Col>
          <Col md={10}>
            <Typography variant="base" color="light">
              {item.institution?.code} - {item.institution?.name}
            </Typography>
          </Col>
        </Row>
        <Row>
          <Col md={2}>
            <Typography variant="base" color="light">
              {t("common.expertCommission.dateFromDateTo")}
            </Typography>
          </Col>
          <Col md={4}>
            <Typography variant="base" color="light">
              {item.dateFrom ? hrDate(new Date(item.dateFrom)) : ""} -{" "}
              {item.dateTo ? hrDate(new Date(item.dateTo)) : ""}
            </Typography>
          </Col>
          <Col md={2}>
            <Typography variant="base" color="light">
              {t("common.status")}
            </Typography>
          </Col>
          <Col md={4}>
            <Label
              color={FormatUtils.getCommitteeStatusColor(item.statusId)}
              className="text-center"
              outline={false}
              fullWidth={false}
            >
              {statuses.find((v) => v.id === item.statusId)?.name}
            </Label>
          </Col>
        </Row>
      </FormCard>

      <FormCard>
        <Table {...tableProps} />
      </FormCard>

      <FormCard>
        <Row>
          <Col md={12}>
            <TextArea
              ariaLabel="item.note"
              label={t("common.expertCommission.note")}
              name={"item.note"}
              value={item.note ?? ""}
              maxLength={5000}
              disabled
            />
          </Col>
        </Row>
      </FormCard>
      <ToolbarEdit showCustom={true}>
        <Button
          className="mr-1 float-right"
          color="primary"
          outline={true}
          size="sm"
          onClick={handleForApprovalClicked}
          disabled={item.statusId !== CommitteeStatus.NotDefined}
        >
          {t("common.expertCommission.forApproval")}
        </Button>
      </ToolbarEdit>
    </ContentArea>
  );
}
